import { useCallback, useEffect, useState } from 'react';
import { Factory } from '../lib/icons';
import { fmt, PageTitle } from '../components/shared/badges';
import { couleurCout, couleurGain, DEFAVORABLE, NEUTRE, NEUTRE_DOUX, signe } from '../lib/couleurs';
import { useAuth } from '../lib/auth';
import {
  bornesDeCampagne,
  campagneCourante,
  fetchChargesIndustrielles,
  fetchFichesOf,
  fetchRegularisations,
  fetchStandards,
  resultatCampagne,
} from '../lib/cri';
import { StandardsCri } from '../components/cri/StandardsCri';
import { ChargesCri } from '../components/cri/ChargesCri';
import { FichesCri } from '../components/cri/FichesCri';
import { RegularisationCri } from '../components/cri/RegularisationCri';
import { TestsCri } from '../components/cri/TestsCri';

const ONGLETS = [
  ['fiches', 'Fiches OF (A → B → C → D)'],
  ['standards', 'Standards industriels'],
  ['charges', 'Charges réelles'],
  ['regularisation', 'Rapprochement & clôture'],
  ['resultat', 'Résultat de campagne'],
  ['tests', 'Tests métier'],
];

const Bloc = ({ titre, items, testid }) => (
  <section data-testid={testid} className="border border-slate-200 bg-white">
    <p className="border-b border-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{titre}</p>
    <div className="divide-y divide-slate-100">
      {items.map(([k, v, cls, note]) => (
        <div key={k} className="flex items-start justify-between gap-4 px-4 py-2">
          <div>
            <p className="text-sm text-slate-700">{k}</p>
            {note && <p className="text-[11px] text-slate-500">{note}</p>}
          </div>
          <p className={`tabular whitespace-nowrap font-data text-sm font-semibold ${cls ?? NEUTRE}`}>{v}</p>
        </div>
      ))}
    </div>
  </section>
);

export default function CoutRevient() {
  const { user } = useAuth();
  const [onglet, setOnglet] = useState('fiches');
  const [campagne, setCampagne] = useState(campagneCourante());
  const [standards, setStandards] = useState([]);
  const [charges, setCharges] = useState([]);
  const [fiches, setFiches] = useState([]);
  const [regularisations, setRegularisations] = useState([]);
  const [resultat, setResultat] = useState(null);

  const load = useCallback(() => {
    fetchStandards().then(setStandards);
    fetchChargesIndustrielles({ campagne }).then(setCharges);
    fetchFichesOf({ campagne }).then(setFiches);
    fetchRegularisations({ campagne }).then(setRegularisations);
    resultatCampagne({ campagne }).then(setResultat);
  }, [campagne]);

  useEffect(() => {
    load();
  }, [load]);

  const bornes = bornesDeCampagne(campagne);
  const annee = Number(String(campagne).split('/')[0]) || new Date().getFullYear();
  const campagnes = [annee - 2, annee - 1, annee].map((a) => `${a}/${a + 1}`);

  return (
    <div data-testid="page-cout-revient" className="space-y-5">
      <PageTitle
        emoji="🏭"
        titre="Coût de Revient Industriel (CRI)"
        sous={`Comptabilité analytique de campagne ${campagne} (${bornes.debut} → ${bornes.fin}) — coût matière et coût de transformation strictement séparés. Le CMP Achat des cuves n’est jamais modifié par ce module.`}
        action={
          <label className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Campagne</span>
            <select
              data-testid="cri-campagne"
              value={campagne}
              onChange={(e) => setCampagne(e.target.value)}
              className="h-11 border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white"
            >
              {campagnes.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
        }
      />

      {resultat && (
        <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-6">
          {[
            ['Olives traitées', `${fmt(resultat.tonnes, 2)} t`, NEUTRE],
            ['Huile produite', `${fmt(resultat.huile, 2)} kg`, NEUTRE],
            ['Coût matière', `${fmt(resultat.matiere, 2)} TND`, DEFAVORABLE],
            ['Frais industriels définitifs', `${fmt(resultat.fraisDefinitifs, 2)} TND`, DEFAVORABLE],
            ['CRI moyen régularisé', resultat.criMoyen === null ? 'Non calculable' : `${fmt(resultat.criMoyen, 2)} TND/kg`, NEUTRE],
            [
              'Marge industrielle régularisée',
              `${signe(resultat.margeIndustrielleRegularisee)} ${fmt(Math.abs(resultat.margeIndustrielleRegularisee), 2)} TND`,
              couleurGain(resultat.margeIndustrielleRegularisee, NEUTRE),
            ],
          ].map(([k, v, cls]) => (
            <div key={k} className="bg-white px-3 py-2.5">
              <p className="text-[11px] text-slate-500">{k}</p>
              <p className={`tabular font-data text-base font-semibold ${cls}`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      <nav data-testid="cri-onglets" className="flex flex-wrap gap-1 border-b border-slate-200">
        {ONGLETS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            data-testid={`cri-onglet-${k}`}
            onClick={() => setOnglet(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors duration-150 ${
              onglet === k ? 'border-slate-900 font-semibold text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {onglet === 'fiches' && <FichesCri fiches={fiches} utilisateur={user?.name ?? user?.email} onMaj={load} />}
      {onglet === 'standards' && <StandardsCri standards={standards} campagne={campagne} utilisateur={user?.name ?? user?.email} onMaj={load} />}
      {onglet === 'charges' && <ChargesCri charges={charges} campagne={campagne} utilisateur={user?.name ?? user?.email} onMaj={load} />}
      {onglet === 'regularisation' && (
        <RegularisationCri campagne={campagne} regularisations={regularisations} utilisateur={user?.name ?? user?.email} onMaj={load} />
      )}
      {onglet === 'tests' && <TestsCri />}

      {onglet === 'resultat' && resultat && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Bloc
            testid="cri-resultat-industriel"
            titre="Résultat industriel au standard puis régularisé"
            items={[
              ['A — Coût olives pur (matière)', `${fmt(resultat.matiere, 2)} TND`, DEFAVORABLE, 'Montants d’achat convenus, avant retenues et avances.'],
              ['Valeur commerciale estimée des huiles produites', `${fmt(resultat.valeurEstimee, 2)} TND`, NEUTRE, `Valorisation au prix de référence du ${new Date().toISOString().slice(0, 10)} — marge estimée, non réalisée.`],
              ['Produit des prestations de trituration', `${fmt(resultat.produitPrestations, 2)} TND`, NEUTRE, 'HT, après remises ; hors taxes collectées pour l’État.'],
              ['B — Marge brute matière', `${signe(resultat.margeBruteMatiere)} ${fmt(Math.abs(resultat.margeBruteMatiere), 2)} TND`, couleurGain(resultat.margeBruteMatiere, NEUTRE)],
              ['C — Frais industriels standards', `${fmt(resultat.fraisStandard, 2)} TND`, DEFAVORABLE],
              ['Régularisation industrielle (ΔF)', `${signe(resultat.regularisation)} ${fmt(Math.abs(resultat.regularisation), 2)} TND`, couleurCout(resultat.regularisation, NEUTRE)],
              ['D — Marge nette industrielle au standard', `${signe(resultat.margeIndustrielleStandard)} ${fmt(Math.abs(resultat.margeIndustrielleStandard), 2)} TND`, couleurGain(resultat.margeIndustrielleStandard, NEUTRE)],
              [
                'Marge nette industrielle régularisée',
                `${signe(resultat.margeIndustrielleRegularisee)} ${fmt(Math.abs(resultat.margeIndustrielleRegularisee), 2)} TND`,
                couleurGain(resultat.margeIndustrielleRegularisee, NEUTRE),
                'Avant frais commerciaux, financiers et impôt sur le résultat.',
              ],
            ]}
          />

          <div className="space-y-4">
            <Bloc
              testid="cri-resultat-exceptionnel"
              titre="Éléments présentés séparément"
              items={[
                ['Charges exceptionnelles (hors CRI)', `${fmt(resultat.exceptionnelles, 2)} TND`, DEFAVORABLE, 'Pertes exceptionnelles isolées du coût standard.'],
                [
                  'Frais fixes non absorbés (sous-activité)',
                  `${fmt(resultat.sousActivite.fixesNonAbsorbes, 2)} TND`,
                  DEFAVORABLE,
                  resultat.sousActivite.tauxActivite === null ? 'Capacité normale non paramétrée.' : `Taux d’activité ${fmt(resultat.sousActivite.tauxActivite * 100, 2)} %.`,
                ],
                [
                  'Écart réel / standard non encore régularisé',
                  `${signe(resultat.ecartNonRegularise)} ${fmt(Math.abs(resultat.ecartNonRegularise), 2)} TND`,
                  couleurCout(resultat.ecartNonRegularise, NEUTRE),
                  'À solder par une régularisation mensuelle ou la clôture de campagne.',
                ],
              ]}
            />

            <Bloc
              testid="cri-resultat-realise"
              titre="Marge réalisée sur ventes"
              items={[
                ['Huile vendue (campagne)', `${fmt(resultat.kgVendus, 2)} kg`, NEUTRE],
                ['Chiffre d’affaires encaissable', `${fmt(resultat.caVentes, 2)} TND`, NEUTRE],
                ['Coût des quantités vendues au CRI régularisé', resultat.coutVendu === null ? 'Non calculable' : `${fmt(resultat.coutVendu, 2)} TND`, DEFAVORABLE],
                [
                  'Marge réalisée',
                  resultat.margeRealisee === null ? 'Non calculable' : `${signe(resultat.margeRealisee)} ${fmt(Math.abs(resultat.margeRealisee), 2)} TND`,
                  resultat.margeRealisee === null ? NEUTRE_DOUX : couleurGain(resultat.margeRealisee, NEUTRE),
                  'Ventes réalisées et coûts des seules quantités vendues.',
                ],
                [
                  'Huile encore en stock',
                  `${fmt(resultat.stockKg, 2)} kg`,
                  NEUTRE,
                  resultat.valeurStockCri === null ? 'Coût non calculable.' : `Coût attaché au stock : ${fmt(resultat.valeurStockCri, 2)} TND.`,
                ],
              ]}
            />

            <Bloc
              testid="cri-resultat-controle"
              titre="Contrôles d’intégrité"
              items={[
                ['Ordres de fabrication de la campagne', `${resultat.nbOfs}`, NEUTRE],
                ['OF au coût standard figé', `${resultat.nbFiges}`, resultat.nbFiges === resultat.nbOfs ? NEUTRE : NEUTRE_DOUX, resultat.nbFiges === resultat.nbOfs ? 'Tous les OF portent une version de standard.' : 'Des OF restent en coût provisoire.'],
                ['Charges réelles incorporées (normales)', `${fmt(resultat.reelles, 2)} TND`, DEFAVORABLE],
                ['Frais standards + régularisations', `${fmt(resultat.fraisDefinitifs, 2)} TND`, NEUTRE],
              ]}
            />
          </div>
        </div>
      )}

      <p className="flex items-start gap-2 border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
        <Factory size={14} className="mt-0.5 flex-shrink-0" />
        Module analytique : les imputations et régularisations ne créent aucune facture fournisseur, aucun paiement et aucune écriture de caisse. Le CMP Achat des cuves reste la moyenne pondérée des seules valeurs matière, et le simulateur d’achat à la bascule n’utilise que le coût matière et le rendement anticipé.
      </p>
    </div>
  );
}
